import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainSidebar from "components/MainSidebar";
import Icon from "components/AppIcon";
import NetWorthCard from "./components/NetWorthCard";
import EnergyXPPanel from "./components/EnergyXPPanel";
import FactionStatusPanel from "./components/FactionStatusPanel";
import PortfolioSummary from "./components/PortfolioSummary";
import MarketHighlights from "./components/MarketHighlights";
import ActivityFeed from "./components/ActivityFeed";
import QuickActions from "./components/QuickActions";
import StatsRow from "./components/StatsRow";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { StatCardSkeleton, TrendingStockSkeleton, SkeletonRow, ChartSkeleton, EmptyState } from "components/SkeletonLoader";
import QueueStatusIndicator from "../../utils/QueueStatusIndicator";
import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import { flushCache, warmDashboardCache } from "../../utils/redisCache";
import { useInactivityTimeout } from "../../hooks/useInactivityTimeout";
import InactivityWarningModal from "../../components/InactivityWarningModal";

// Welcome screen for brand new users with no stats yet
const NewUserWelcome = ({ username, navigate }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
      style={{ background: "rgba(0,217,255,0.1)", border: "2px solid rgba(0,217,255,0.3)" }}
    >
      <Icon name="TrendingUp" size={36} color="var(--color-primary)" />
    </div>
    <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
      Welcome to EmpireX, {username}!
    </h2>
    <p className="text-muted-foreground text-sm max-w-md mb-8">
      Your account is being set up. Start by exploring the stock exchange, building your portfolio, or joining a faction.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-lg">
      <button
        onClick={() => navigate("/stock-exchange")}
        className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all"
        style={{ background: "var(--color-card)" }}
      >
        <Icon name="BarChart2" size={24} color="var(--color-primary)" />
        <span className="text-sm font-medium text-foreground">Stock Exchange</span>
        <span className="text-xs text-muted-foreground">Start trading</span>
      </button>
      <button
        onClick={() => navigate("/portfolio")}
        className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all"
        style={{ background: "var(--color-card)" }}
      >
        <Icon name="Briefcase" size={24} color="var(--color-accent)" />
        <span className="text-sm font-medium text-foreground">Portfolio</span>
        <span className="text-xs text-muted-foreground">View holdings</span>
      </button>
      <button
        onClick={() => navigate("/factions")}
        className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all"
        style={{ background: "var(--color-card)" }}
      >
        <Icon name="Users" size={24} color="var(--color-success)" />
        <span className="text-sm font-medium text-foreground">Factions</span>
        <span className="text-xs text-muted-foreground">Join a syndicate</span>
      </button>
    </div>
    <p className="mt-8 text-xs text-muted-foreground">
      Your stats will appear here once your account is fully initialized.
    </p>
  </div>
);

// Connection status badge
const LiveStatusBadge = ({ status, lastUpdated }) => {
  const [relativeTime, setRelativeTime] = useState('just now');

  useEffect(() => {
    if (!lastUpdated) return;
    const update = () => {
      const secs = Math.floor((Date.now() - lastUpdated?.getTime()) / 1000);
      if (secs < 5) setRelativeTime('just now');
      else if (secs < 60) setRelativeTime(`${secs}s ago`);
      else setRelativeTime(`${Math.floor(secs / 60)}m ago`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [lastUpdated]);

  const config = {
    connected: { dot: 'var(--color-success)', label: 'Live Updates Active', textColor: 'var(--color-success)', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)' },
    connecting: { dot: '#F59E0B', label: 'Connecting...', textColor: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
    reconnecting: { dot: '#F59E0B', label: 'Reconnecting...', textColor: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
    offline: { dot: '#EF4444', label: 'Offline', textColor: '#EF4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' },
  };
  const c = config?.[status] || config?.connecting;

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded-lg font-caption text-xs"
      style={{ background: c?.bg, border: `1px solid ${c?.border}`, color: c?.textColor }}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status === 'connected' ? 'animate-pulse' : ''}`}
        style={{ background: c?.dot }}
      />
      <span>{c?.label}</span>
      {status === 'connected' && lastUpdated && (
        <span className="opacity-60">· Updated {relativeTime}</span>
      )}
    </div>
  );
};

// Level-up notification toast
const LevelUpToast = ({ level, onDismiss }) => (
  <div
    className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl animate-bounce"
    style={{
      background: 'linear-gradient(135deg, rgba(0,217,255,0.15) 0%, rgba(26,26,46,0.95) 100%)',
      border: '1px solid rgba(0,217,255,0.4)',
      backdropFilter: 'blur(12px)',
    }}
  >
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: 'rgba(0,217,255,0.2)', border: '1px solid rgba(0,217,255,0.4)' }}
    >
      <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
    </div>
    <div>
      <p className="text-sm font-bold text-foreground">Level Up!</p>
      <p className="text-xs text-muted-foreground">You reached Level {level}</p>
    </div>
    <button onClick={onDismiss} className="ml-2 opacity-60 hover:opacity-100 transition-opacity">
      <Icon name="X" size={14} color="var(--color-muted-foreground)" />
    </button>
  </div>
);

// Vault update toast
const VaultUpdateToast = ({ message, onDismiss }) => (
  <div
    className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl"
    style={{
      background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(26,26,46,0.95) 100%)',
      border: '1px solid rgba(16,185,129,0.3)',
      backdropFilter: 'blur(12px)',
      animation: 'slideInActivity 0.3s ease-out',
    }}
  >
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: 'rgba(16,185,129,0.2)' }}
    >
      <Icon name="Vault" size={16} color="var(--color-success)" />
    </div>
    <p className="font-caption text-xs text-foreground">{message}</p>
    <button onClick={onDismiss} className="opacity-60 hover:opacity-100 transition-opacity ml-1">
      <Icon name="X" size={12} color="var(--color-muted-foreground)" />
    </button>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [tickCountdown, setTickCountdown] = useState(42);

  // Initial (static load) data state
  const [initialPlayerStats, setInitialPlayerStats] = useState(null);
  const [initialFaction, setInitialFaction] = useState(null);
  const [initialHoldings, setInitialHoldings] = useState([]);
  const [trendingStocks, setTrendingStocks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [initialNetWorthTrend, setInitialNetWorthTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpValue, setLevelUpValue] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [inactivityMessage, setInactivityMessage] = useState('');

  // Real-time subscriptions
  const {
    playerStats,
    holdings,
    faction,
    realtimeActivities,
    netWorthTrend,
    netWorthChange,
    connectionStatus,
    lastUpdated,
    vaultToast,
    subscriptionHealth,
    addOptimisticActivity,
    manualRefresh,
  } = useDashboardRealtime({
    userId: user?.id,
    initialPlayerStats,
    initialHoldings,
    initialFaction,
    onLevelUp: (level) => {
      setLevelUpValue(level);
      setShowLevelUp(true);
    },
  });

  // ── Logout handler ──────────────────────────────────────────────────────
  const handleLogout = useCallback(async (reason) => {
    setLogoutLoading(true);
    try {
      // Clear all caches
      try { flushCache(); } catch {}

      // Clear storage
      localStorage.removeItem('empirex-last-activity');
      // Clear all supabase auth keys from localStorage
      Object.keys(localStorage)?.forEach(key => {
        if (key?.startsWith('empirex-auth') || key?.startsWith('supabase.auth') || key?.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();

      await signOut();

      if (reason === 'inactivity') {
        navigate('/sign-in', { replace: true, state: { message: 'You were logged out due to inactivity.' } });
      } else {
        navigate('/sign-in', { replace: true });
      }
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/sign-in', { replace: true });
    } finally {
      setLogoutLoading(false);
    }
  }, [signOut, navigate]);

  // ── Inactivity timeout ──────────────────────────────────────────────────
  const { showWarning, sessionExpired, stayLoggedIn, clearSessionExpired } = useInactivityTimeout({
    onLogout: handleLogout,
    enabled: !!user,
  });

  // Show expired session message on mount if applicable
  useEffect(() => {
    if (sessionExpired) {
      setInactivityMessage('Your previous session expired due to inactivity.');
      clearSessionExpired();
    }
  }, [sessionExpired, clearSessionExpired]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      setTickCountdown((prev) => (prev <= 1 ? 60 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setFetchError(null);
    setIsNewUser(false);
    try {
      const [statsRes, memberRes, holdingsRes, stocksRes, tradesRes, crimesRes, notifRes] = await Promise.all([
        supabase?.from('player_stats')?.select('*')?.eq('user_id', user?.id)?.maybeSingle(),
        supabase?.from('faction_members')?.select('rank, factions(faction_name, tag, banner_color, total_vault_balance, faction_xp, territory_count)')?.eq('user_id', user?.id)?.maybeSingle(),
        supabase?.from('portfolio_holdings')?.select('quantity, avg_buy_price, stocks(ticker, company_name, current_price, sector, open_price)')?.eq('user_id', user?.id)?.gt('quantity', 0)?.limit(5),
        supabase?.from('stocks')?.select('ticker, company_name, current_price, open_price, volume')?.eq('is_listed', true)?.order('volume', { ascending: false })?.limit(5),
        supabase?.from('trade_history')?.select('id, order_type, quantity, price, created_at, stocks(ticker)')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(5),
        supabase?.from('crime_attempts')?.select('id, success, reward, created_at, crime_definitions(name)')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(3),
        supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('is_read', false),
      ]);

      // Stats
      if (statsRes?.error) {
        console.error('player_stats fetch error:', statsRes?.error);
        // Don't treat as new user on error — show error state
        setFetchError(statsRes?.error);
        setLoading(false);
        return;
      }

      if (!statsRes?.data) {
        // Truly new user — no stats row yet
        supabase?.rpc('initialize_player_stats')?.catch(() => {});
        setIsNewUser(true);
        setLoading(false);
        return;
      }

      setInitialPlayerStats(statsRes?.data);

      // Faction — null means not in faction (not an error)
      if (!memberRes?.error && memberRes?.data?.factions) {
        const f = memberRes?.data?.factions;
        setInitialFaction({
          name: f?.faction_name || 'Unknown Faction',
          rank: memberRes?.data?.rank || 'Member',
          vault: parseFloat(f?.total_vault_balance || 0),
          territories: f?.territory_count || 0,
          territoryBonus: 0,
          color: f?.banner_color || '#00D9FF',
        });
      } else {
        // Not in a faction — set to null explicitly
        setInitialFaction(null);
      }

      // Holdings — empty array is valid (no holdings yet)
      if (!holdingsRes?.error) {
        const mapped = (holdingsRes?.data || [])?.map(h => ({
          ticker: h?.stocks?.ticker || 'N/A',
          name: h?.stocks?.company_name || 'Unknown',
          shares: parseInt(h?.quantity || 0),
          avgBuyPrice: parseFloat(h?.avg_buy_price || 0),
          currentPrice: parseFloat(h?.stocks?.current_price || 0),
          value: parseInt(h?.quantity || 0) * parseFloat(h?.stocks?.current_price || 0),
          pnl: (parseFloat(h?.stocks?.current_price || 0) - parseFloat(h?.avg_buy_price || 0)) * parseInt(h?.quantity || 0),
          change: h?.stocks?.open_price > 0
            ? parseFloat(((h?.stocks?.current_price - h?.stocks?.open_price) / h?.stocks?.open_price * 100)?.toFixed(2))
            : 0,
        }));
        setInitialHoldings(mapped);
      } else {
        console.error('portfolio_holdings fetch error:', holdingsRes?.error);
        setInitialHoldings([]);
      }

      // Trending stocks — top 5 by volume
      if (!stocksRes?.error) {
        setTrendingStocks((stocksRes?.data || [])?.map(s => ({
          ticker: s?.ticker || 'N/A',
          name: s?.company_name || 'Unknown',
          price: parseFloat(s?.current_price || 0),
          change: s?.open_price > 0
            ? parseFloat(((s?.current_price - s?.open_price) / s?.open_price * 100)?.toFixed(2))
            : 0,
        })));
      } else {
        console.error('stocks fetch error:', stocksRes?.error);
        setTrendingStocks([]);
      }

      // Activity feed
      const acts = [];
      (tradesRes?.data || [])?.forEach((t) => {
        acts?.push({
          id: `trade-${t?.id}`,
          type: 'trade',
          message: `${t?.order_type === 'buy' ? 'Bought' : 'Sold'} ${t?.quantity} ${t?.stocks?.ticker || 'shares'} @ $${parseFloat(t?.price || 0)?.toFixed(2)}`,
          detail: null,
          outcome: 'success',
          time: new Date(t?.created_at)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        });
      });
      (crimesRes?.data || [])?.forEach(c => {
        acts?.push({
          id: `crime-${c?.id}`,
          type: 'crime',
          message: `${c?.crime_definitions?.name || 'Crime attempt'}`,
          detail: c?.success ? `SUCCESS — +$${parseFloat(c?.reward || 0)?.toLocaleString()}` : 'FAILED',
          outcome: c?.success ? 'success' : 'failure',
          time: new Date(c?.created_at)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        });
      });
      acts?.sort((a, b) => b?.id?.localeCompare(a?.id));
      setActivities(acts?.slice(0, 7));

      setNotificationCount(notifRes?.count || 0);

      const nw = parseFloat(statsRes?.data?.net_worth || 0);
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      setInitialNetWorthTrend(days?.map((label, i) => ({
        label,
        value: nw * (0.92 + i * 0.013 + (Math.random() * 0.01)),
      })));

    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  // Cache warming on mount
  useEffect(() => {
    warmDashboardCache().catch(() => {});
  }, []);

  // Derive display values from realtime-merged data
  const username = userProfile?.username || user?.email?.split('@')?.[0] || 'Commander';
  const level = playerStats?.level || 1;
  const energy = typeof playerStats?.energy === 'number' ? playerStats?.energy : 0;
  const maxEnergy = typeof playerStats?.max_energy === 'number' ? playerStats?.max_energy : 100;
  const xp = parseFloat(playerStats?.xp || 0);
  const xpToNext = parseFloat(playerStats?.xp_to_next || 1000);
  const netWorth = parseFloat(playerStats?.net_worth || 0);
  const cash = parseFloat(playerStats?.cash || 0);
  const totalPortfolioValue = (holdings || [])?.reduce((s, h) => s + (h?.value || 0), 0);

  // Use realtime activities if available, else fall back to initial fetch
  const displayActivities = realtimeActivities ?? activities;

  // Use realtime net worth trend if available, else fall back to initial
  const displayNetWorthTrend = netWorthTrend?.length > 0 ? netWorthTrend : initialNetWorthTrend;

  const stats = {
    totalTrades: playerStats?.total_trades || 0,
    businesses: 0,
    businessRevenue: 0,
    crimeSuccessRate: 0,
    crimesCompleted: playerStats?.total_crimes || 0,
    globalRank: 0,
  };

  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 72 : 280;

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--color-background)" }}
    >
      {/* Slide-in animation for activity feed */}
      <style>{`
        @keyframes slideInActivity {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Inactivity Warning Modal */}
      {showWarning && (
        <InactivityWarningModal
          onStayLoggedIn={stayLoggedIn}
          onLogoutNow={() => handleLogout('manual')}
        />
      )}

      {/* Vault update toast */}
      {vaultToast && (
        <VaultUpdateToast
          message={vaultToast}
          onDismiss={() => {}}
        />
      )}

      {/* Level-up toast */}
      {showLevelUp && levelUpValue && (
        <LevelUpToast level={levelUpValue} onDismiss={() => setShowLevelUp(false)} />
      )}

      {/* Sidebar */}
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
        userRole="player"
        jailStatus={!!(playerStats?.jail_until && new Date(playerStats?.jail_until) > new Date())}
        jailTimer={playerStats?.jail_until ? Math.max(0, Math.round((new Date(playerStats?.jail_until) - new Date()) / 1000)) : 0}
        notificationCounts={{ notifications: notificationCount }}
        userStats={{ username, level, energy, maxEnergy, xp, xpToNext }}
      />

      {/* Main Content */}
      <div
        className="transition-all duration-250"
        style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          paddingBottom: isMobile ? "80px" : "0",
        }}
      >
        {/* Top Bar */}
        <header
          className="sticky top-0 z-50 flex items-center justify-between px-4 md:px-6 lg:px-8 h-14 border-b border-border"
          style={{ background: "var(--color-secondary)" }}
        >
          <div className={`flex items-center gap-3 ${isMobile ? "ml-12" : ""}`}>
            <h1 className="font-heading text-sm md:text-base font-bold text-foreground">
              Command Center
            </h1>
            {/* Live Updates Status Badge */}
            <div className="hidden md:block">
              <LiveStatusBadge status={connectionStatus} lastUpdated={lastUpdated} />
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Queue Status */}
            <QueueStatusIndicator className="hidden sm:flex" />

            {/* Tick Timer */}
            <div
              className="hidden sm:flex items-center gap-1.5 font-caption text-xs px-2.5 py-1 rounded-md"
              style={{
                background: "var(--color-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <Icon name="Timer" size={12} color="var(--color-accent)" />
              <span className="text-muted-foreground">Next tick:</span>
              <span className="text-accent font-medium">{tickCountdown}s</span>
            </div>

            {/* Time */}
            <div className="hidden md:block font-caption text-xs text-muted-foreground">
              {currentTime?.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </div>

            {/* Notifications */}
            <button
              onClick={() => navigate("/notifications")}
              aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
              className="relative w-9 h-9 rounded-lg flex items-center justify-center hover:bg-primary/10 transition-all duration-200"
              style={{ border: "1px solid var(--color-border)" }}
            >
              <Icon name="Bell" size={16} color="var(--color-muted-foreground)" />
              {notificationCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full font-caption text-xs flex items-center justify-center"
                  style={{
                    background: "var(--color-primary)",
                    color: "var(--color-primary-foreground)",
                    fontSize: "9px",
                  }}
                >
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>

            {/* Logout Button */}
            <button
              onClick={() => handleLogout('manual')}
              disabled={logoutLoading}
              aria-label="Logout"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-caption text-xs font-medium transition-all duration-200 hover:bg-error/10 hover:text-error disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                border: "1px solid var(--color-border)",
                color: "var(--color-muted-foreground)",
              }}
            >
              {logoutLoading ? (
                <Icon name="Loader2" size={14} className="animate-spin" />
              ) : (
                <Icon name="LogOut" size={14} />
              )}
              <span className="hidden sm:inline">{logoutLoading ? 'Logging out...' : 'Logout'}</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="px-4 md:px-6 lg:px-8 py-4 md:py-6">

          {/* Inactivity expired message */}
          {inactivityMessage && (
            <div className="mb-4 p-3 rounded-xl border border-warning/20 bg-warning/5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={15} color="#F59E0B" />
                <span className="text-sm text-foreground">{inactivityMessage}</span>
              </div>
              <button onClick={() => setInactivityMessage('')} className="opacity-60 hover:opacity-100">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
          )}

          {/* New User Welcome Screen */}
          {!loading && isNewUser && (
            <NewUserWelcome username={username} navigate={navigate} />
          )}

          {/* Normal Dashboard (existing users with stats) */}
          {!isNewUser && (
            <>
              {/* Welcome Banner */}
              <div
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 p-4 rounded-xl border border-border"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(0,217,255,0.06) 0%, rgba(26,26,46,0.8) 100%)",
                  borderColor: "rgba(0,217,255,0.15)",
                }}
              >
                <div>
                  <h2 className="font-heading text-base md:text-lg font-bold text-foreground">
                    Welcome back, {username}
                  </h2>
                  <p className="font-caption text-xs text-muted-foreground mt-0.5">
                    {currentTime?.toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                    {" · "}Tick in {tickCountdown}s
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Mobile live status */}
                  <div className="md:hidden">
                    <LiveStatusBadge status={connectionStatus} lastUpdated={lastUpdated} />
                  </div>
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-caption text-xs"
                    style={{
                      background: "rgba(16,185,129,0.1)",
                      color: "var(--color-success)",
                      border: "1px solid rgba(16,185,129,0.2)",
                    }}
                  >
                    <Icon name="ShieldCheck" size={12} color="var(--color-success)" />
                    Free
                  </div>
                </div>
              </div>

              {/* Error State with Retry */}
              {fetchError && !loading && (
                <div className="mb-5 p-4 rounded-xl border border-error/20 bg-error/5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error, #EF4444)" />
                    <span className="text-sm text-foreground">
                      Failed to load dashboard data. Please try again.
                    </span>
                  </div>
                  <button
                    onClick={fetchDashboardData}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium hover:bg-primary hover:text-zinc-950 transition-all"
                  >
                    <Icon name="RefreshCw" size={12} />
                    Retry
                  </button>
                </div>
              )}

              {/* Stats Row */}
              <div className="mb-5">
                {loading ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Array(4)?.fill(0)?.map((_, i) => <StatCardSkeleton key={i} />)}
                  </div>
                ) : (
                  <StatsRow stats={stats} />
                )}
              </div>

              {/* Main Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-5">
                {/* Left Column — Net Worth + Portfolio */}
                <div className="lg:col-span-2 space-y-4 md:space-y-5">
                  {loading ? (
                    <ChartSkeleton height={180} />
                  ) : (
                    <NetWorthCard
                      netWorth={netWorth}
                      change={netWorthChange?.change ?? 0}
                      changePercent={netWorthChange?.changePercent ?? 0}
                      trendData={displayNetWorthTrend?.length > 0 ? displayNetWorthTrend : []}
                      isLive={connectionStatus === 'connected'}
                      onRefresh={connectionStatus !== 'connected' ? manualRefresh : undefined}
                    />
                  )}
                  {loading ? (
                    <ChartSkeleton height={160} />
                  ) : (
                    <PortfolioSummary
                      holdings={holdings || []}
                      totalValue={totalPortfolioValue}
                      dailyPnl={[]}
                      weeklyPnl={[]}
                    />
                  )}
                  <QuickActions />
                </div>

                {/* Right Column — Status + Faction + Market */}
                <div className="space-y-4 md:space-y-5">
                  {loading ? (
                    <ChartSkeleton height={140} />
                  ) : (
                    <EnergyXPPanel
                      energy={energy}
                      maxEnergy={maxEnergy}
                      xp={xp}
                      xpToNext={xpToNext}
                      level={level}
                      regenTime=""
                    />
                  )}
                  {loading ? (
                    <ChartSkeleton height={140} />
                  ) : (
                    <FactionStatusPanel faction={faction} isLive={connectionStatus === 'connected'} />
                  )}
                  {loading ? (
                    <div className="rounded-xl border border-border bg-card p-4">
                      <div className="h-4 w-28 rounded bg-white/5 animate-pulse mb-3" />
                      <div className="space-y-2">
                        {Array(5)?.fill(0)?.map((_, i) => <TrendingStockSkeleton key={i} />)}
                      </div>
                    </div>
                  ) : (
                    <MarketHighlights
                      stocks={trendingStocks}
                      economicEvents={[]}
                    />
                  )}
                </div>
              </div>

              {/* Activity Feed */}
              {loading ? (
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="h-4 w-28 rounded bg-white/5 animate-pulse mb-4" />
                  <table className="w-full">
                    <tbody>
                      {Array(5)?.fill(0)?.map((_, i) => <SkeletonRow key={i} cols={4} />)}
                    </tbody>
                  </table>
                </div>
              ) : displayActivities?.length === 0 ? (
                <div className="rounded-xl border border-border bg-card">
                  <EmptyState
                    icon="Activity"
                    title="No recent activity"
                    description="Start trading to see your history"
                    action={() => navigate("/stock-exchange")}
                    actionLabel="Start Trading"
                  />
                </div>
              ) : (
                <ActivityFeed activities={displayActivities} isLive={connectionStatus === 'connected'} />
              )}

              {/* Footer */}
              <footer className="mt-6 pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-2">
                <p className="font-caption text-xs text-muted-foreground">
                  © {new Date()?.getFullYear()} NEXUS Economic Terminal. All rights reserved.
                </p>
                <div className="flex items-center gap-3">
                  <LiveStatusBadge status={connectionStatus} lastUpdated={lastUpdated} />
                  <span className="font-caption text-xs text-muted-foreground">
                    v2.4.1
                  </span>
                </div>
              </footer>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;